import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  showOnboarding,
  hideOnboarding,
  nextStep,
  previousStep,
  goToStep,
  completeStep,
  resetOnboarding,
  setOnboardingData
} from '../app/onboarding';
import {
  hasSeenOnboarding,
  markOnboardingAsSeen,
  trackOnboardingStep,
  getOnboardingDataForBackend,
  clearOnboardingData,
  generateSessionId
} from '../utils/onboardingStorage';
import { updateOnboardingCompletion } from '../utils/newBackendApi';
import { auth } from '../utils/firebase';

export const useOnboarding = () => {
  const dispatch = useDispatch();
  const onboardingState = useSelector(state => state.onboarding || {});

  const {
    isVisible = false,
    currentStep = 1,
    totalSteps = 3,
    completedSteps = [],
    isComplete = false,
    userData = {},
    steps = []
  } = onboardingState;

  const currentStepData = steps.find(step => step.id === currentStep) || {};

  const checkAndShowOnboarding = useCallback((userId = null, email = null) => {
    const hasSeen = hasSeenOnboarding();
    
    if (!hasSeen) {
      dispatch(setOnboardingData({
        userId,
        email,
        sessionId: generateSessionId(),
        userType: userId ? 'registered' : 'anonymous'
      }));
      dispatch(showOnboarding());
      return true;
    }
    return false;
  }, [dispatch]);

  const handleNextStep = useCallback(async () => {
    const nextStepNumber = currentStep + 1;
    
    dispatch(completeStep(currentStep));
    
    try {
      await trackOnboardingStep(currentStep, userData.userId, userData.email);
      
      await sendToBackend({
        ...userData,
        completedSteps: [...completedSteps, currentStep],
        currentStep,
        action: 'step_completed'
      });
    } catch (error) {
      console.error('Error tracking onboarding step:', error);
    }

    if (nextStepNumber <= totalSteps) {
      dispatch(nextStep());
    } else {
      // Llamar directamente las acciones necesarias para completar
      try {
        markOnboardingAsSeen(userData.userId, userData.email);
        
        const finalData = getOnboardingDataForBackend();
        await sendToBackend({
          ...finalData,
          completedSteps: [...completedSteps, currentStep],
          action: 'onboarding_completed'
        });
        
        dispatch(hideOnboarding());
      } catch (error) {
        console.error('Error completing onboarding:', error);
        dispatch(hideOnboarding());
      }
    }
  }, [currentStep, totalSteps, completedSteps, userData, dispatch]);

  const handlePreviousStep = useCallback(() => {
    dispatch(previousStep());
  }, [dispatch]);

  const handleGoToStep = useCallback((stepNumber) => {
    dispatch(goToStep(stepNumber));
  }, [dispatch]);

  const handleComplete = useCallback(async () => {
    try {
      // Primero marcar el último paso como completado
      dispatch(completeStep(currentStep));
      
      // Marcar como visto en localStorage
      markOnboardingAsSeen(userData.userId, userData.email);
      
      // Enviar datos al backend
      const finalData = getOnboardingDataForBackend();
      await sendToBackend({
        ...finalData,
        completedSteps: [...completedSteps, currentStep],
        action: 'onboarding_completed'
      });
      
      // ⭐ NEW: Update onboarding completion in new backend
      const user = auth.currentUser;
      if (user) {
        updateOnboardingCompletion(user.uid).catch(error => {
          console.warn('Failed to update onboarding completion in new backend:', error);
        });
      }
      
      // Cerrar el modal
      dispatch(hideOnboarding());
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // Aún así cerrar el modal en caso de error
      dispatch(hideOnboarding());
    }
  }, [userData, dispatch, currentStep, completedSteps]);

  const handleClose = useCallback(async () => {
    try {
      const partialData = getOnboardingDataForBackend() || userData;
      await sendToBackend({
        ...partialData,
        completedSteps,
        currentStep,
        action: 'onboarding_skipped'
      });
      
      markOnboardingAsSeen(userData.userId, userData.email);
    } catch (error) {
      console.error('Error closing onboarding:', error);
    } finally {
      // Siempre cerrar el modal sin importar si hay error
      dispatch(hideOnboarding());
    }
  }, [completedSteps, currentStep, userData, dispatch]);

  const handleReset = useCallback(() => {
    clearOnboardingData();
    dispatch(resetOnboarding());
  }, [dispatch]);

  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === totalSteps;
  const canGoNext = currentStep < totalSteps;
  const canGoPrevious = currentStep > 1;

  // Función de desarrollo para forzar mostrar onboarding
  const forceShowOnboarding = useCallback((userId = null, email = null) => {
    console.log('useOnboarding: FORCE showing onboarding for development');
    clearOnboardingData(); // Limpiar localStorage
    dispatch(resetOnboarding()); // Reset Redux state
    dispatch(setOnboardingData({
      userId,
      email,
      sessionId: generateSessionId(),
      userType: userId ? 'registered' : 'anonymous'
    }));
    dispatch(showOnboarding());
  }, [dispatch]);

  return {
    isVisible,
    currentStep,
    totalSteps,
    completedSteps,
    isComplete,
    userData,
    steps,
    currentStepData,
    isFirstStep,
    isLastStep,
    canGoNext,
    canGoPrevious,
    checkAndShowOnboarding,
    nextStep: handleNextStep,
    previousStep: handlePreviousStep,
    goToStep: handleGoToStep,
    complete: handleComplete,
    close: handleClose,
    reset: handleReset,
    forceShowOnboarding // Para development
  };
};


const sendToBackend = async (data) => {
  try {
    console.log('Sending onboarding data to backend:', data);
    
    // TODO: Implementar la llamada real al backend cuando esté disponible
    // const response = await fetch('/api/onboarding-tracking', {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //   },
    //   body: JSON.stringify(data)
    // });
    
    // if (!response.ok) {
    //   throw new Error('Failed to send onboarding data');
    // }
    
    // return response.json();
    
    return Promise.resolve(data);
  } catch (error) {
    console.error('Error sending data to backend:', error);
    throw error;
  }
};